/**
 * Templates — what a document of a given type LOOKS like when it is written.
 *
 * A template is design-time content: page, typefaces, a formatting rule for
 * every element, the document control section, and the sections a new document
 * opens with. It lives in the repository model file rather than in a runtime
 * table because it is what a DESIGNER authored, not a per-tenant value an
 * administrator sets — the same reason the rights matrix lives there and the
 * document types do not.
 *
 * Two things here are worth stating rather than discovering:
 *
 * **The control section is bound, not typed.** Each row names WHERE its value
 * comes from — a metadata field, a document property, fixed wording, or the
 * author — and the value is resolved from the record on every open and every
 * render. That is what stops a document whose version box says 1.2 while the
 * repository serves 2.0.
 *
 * **Editing a template does not reflow documents already written against it.**
 * A saved document embeds the template it was written with, so a change here
 * governs documents written from now on. Repaginating an approved SOP because
 * somebody adjusted a margin would be the worse behaviour by far.
 */

import { useCallback, useMemo, useState } from 'react'
import {
  ELEMENT_LABELS, FONT_FACES,
  type DocumentTemplate, type FormattingRule,
} from '@twasta/page-runtime/dms/authoring'
import {
  btnStyle as dmsBtnStyle, cardStyle as dmsCardStyle,
  inputStyle as dmsInputStyle, labelStyle as dmsLabelStyle,
  RADIUS, T, TYPE,
} from '@twasta/page-runtime/dms'

const CONTROL_SOURCES = [
  { value: 'document', label: 'A document property' },
  { value: 'metadata', label: 'A metadata field' },
  { value: 'static', label: 'Fixed wording' },
  { value: 'author', label: 'The author fills it in' },
]

const DOCUMENT_KEYS = [
  'title', 'doctype', 'version', 'state', 'document_id', 'effective_date',
  'created_by', 'created_at', 'updated_at', 'owner', 'repository',
]

const LIST_NUMBERING = [
  { value: 'legal', label: '1 / 1.1 / 1.1.1' },
  { value: 'decimal', label: '1. then a. then i.' },
  { value: 'alpha', label: 'a. b. c.' },
  { value: 'alphaUpper', label: 'A. B. C.' },
  { value: 'roman', label: 'i. ii. iii.' },
  { value: 'romanUpper', label: 'I. II. III.' },
]

/**
 * The key a template is actually SAVED under.
 *
 * `authoring._slug` on the backend rewrites anything that is not a letter,
 * digit or underscore, upper-cases, and falls back to GENERAL when nothing
 * survives — and `templates_of` then drops a template whose key duplicates
 * one already seen. Between them, a template whose Key field is left empty or
 * filled with punctuation is silently merged into GENERAL and DISCARDED on
 * save: the designer comes back one template shorter with nothing said. The
 * rule is mirrored here so the tab can show the key that will be stored and
 * refuse to let one be minted that the save would swallow.
 */
function templateKey(raw: string): string {
  return String(raw || '').replace(/[^A-Za-z0-9_]+/g, '_')
    .replace(/^_+|_+$/g, '').toUpperCase().slice(0, 60)
}

/** Elements offered in the rule editor, grouped so the list is navigable. */
const RULE_GROUPS: Array<{ title: string; elements: string[] }> = [
  { title: 'Headings', elements: ['title', 'subtitle', 'heading1', 'heading2', 'heading3', 'heading4'] },
  { title: 'Body', elements: ['body', 'quote', 'code', 'caption', 'footnote'] },
  { title: 'Lists', elements: ['listBullet', 'listNumber'] },
  { title: 'Tables', elements: ['tableHeader', 'tableCell'] },
  { title: 'Document control', elements: ['controlLabel', 'controlValue'] },
]

export function TemplatesTab({ repository, patch, doctypes }: {
  repository: Record<string, any>
  patch: (path: string[], value: unknown) => void
  doctypes: any[]
}) {
  const templates: DocumentTemplate[] = repository.templates || []
  const [selected, setSelected] = useState(0)
  const [element, setElement] = useState('body')
  const template = templates[selected]

  const set = useCallback((path: string[], value: unknown) => {
    patch(['repository', 'templates', String(selected), ...path], value)
  }, [patch, selected])

  /** Which document types would be affected by a change to this template. */
  const usedBy = useMemo(() => (template
    ? doctypes.filter((t) => (t.authoring?.mode || 'off') !== 'off'
      && templateKey(String(t.authoring?.template || 'GENERAL'))
        === templateKey(template.key))
    : []), [doctypes, template])

  /**
   * What saving would do to this template's key — and whether saving would
   * lose it. Both failure modes are silent on the way through: an empty or
   * punctuation-only key becomes GENERAL, and a key that duplicates another
   * template's is dropped. Neither should be discovered by the template not
   * being there afterwards.
   */
  const keyState = useMemo(() => {
    if (!template) return null
    const stored = templateKey(template.key)
    const clash = templates.some((t, i) => i !== selected
      && templateKey(t.key) === (stored || 'GENERAL'))
    if (!stored) {
      return { stored: 'GENERAL', problem: 'This template has no key that '
        + 'survives saving, so it would be filed as GENERAL'
        + (clash ? ' — and discarded, because another template already holds '
          + 'that key' : '')
        + '. Give it a key of letters, digits and underscores.' }
    }
    if (clash) {
      return { stored, problem: `Another template is already saved under `
        + `${stored}. Only the first of a repeated key is kept, so this one `
        + `would be discarded on save. Give it a key of its own.` }
    }
    return { stored, problem: '' }
  }, [template, templates, selected])

  const addTemplate = () => {
    const base = templates[0]
    if (!base) return
    // The first FREE number, not the count: renaming a template to the name the
    // count would next produce made "Add template" mint a duplicate, and a
    // duplicate key is dropped on save.
    const taken = new Set(templates.map((t) => templateKey(t.key)))
    let n = templates.length + 1
    while (taken.has(`TEMPLATE_${n}`)) n += 1
    const key = `TEMPLATE_${n}`
    patch(['repository', 'templates'], [...templates, {
      // Copied from the general template rather than built from nothing: an
      // empty template is a page an author cannot use, and every field in the
      // shape has to be present for the layout engine to read it.
      ...JSON.parse(JSON.stringify(base)),
      key,
      name: 'New template',
      description: '',
    }])
    setSelected(templates.length)
  }

  return (
    <div style={{ display: 'flex', gap: 20, alignItems: 'flex-start' }}>
      <aside style={{ width: 220, flexShrink: 0 }}>
        <h3 style={groupTitle}>Templates</h3>
        <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
          {templates.map((t, index) => (
            <li key={t.key}>
              <button type="button" onClick={() => setSelected(index)}
                style={{ ...listBtn,
                  background: index === selected
                    ? 'var(--color-surface-2)' : 'transparent' }}>
                {t.name || t.key}
              </button>
            </li>
          ))}
        </ul>
        <button type="button" style={{ ...btn, width: '100%', marginTop: 8 }}
          onClick={addTemplate}>Add template</button>
        <p style={help}>
          The general template is always present. It is what a document type
          uses when it names none, so it cannot be removed.
        </p>
      </aside>

      {!template ? (
        <p style={mutedBox}>This repository has no templates.</p>
      ) : (
        <div style={{ flex: 1, minWidth: 0, display: 'grid', gap: 16,
          maxWidth: 760 }}>
          <div style={row}>
            <Labelled label="Key"
              help="Named by a document type. Changing it orphans the types that point here.">
              <input style={keyState?.problem
                ? { ...input, borderColor: T.error } : input}
                value={template.key}
                readOnly={template.key === 'GENERAL'}
                aria-invalid={keyState?.problem ? true : undefined}
                onChange={(e) => set(['key'], e.target.value.toUpperCase())} />
            </Labelled>
            <Labelled label="Name">
              <input style={input} value={template.name}
                onChange={(e) => set(['name'], e.target.value)} />
            </Labelled>
          </div>

          {keyState?.problem ? (
            <p style={{ ...help, color: T.error }}>{keyState.problem}</p>
          ) : keyState && keyState.stored !== template.key ? (
            <p style={help}>Saved as <code>{keyState.stored}</code> — a key is
              letters, digits and underscores.</p>
          ) : null}

          <Labelled label="Description"
            help="Shown to an author when this template is used.">
            <input style={input} value={template.description}
              onChange={(e) => set(['description'], e.target.value)} />
          </Labelled>

          {usedBy.length > 0 ? (
            <p style={help}>
              Used by: {usedBy.map((t) => t.title || t.key).join(', ')}. Changes
              here govern documents written from now on — a document already
              written keeps the template it was written against, so an approved
              document never repaginates because a template was edited.
            </p>
          ) : (
            <p style={help}>
              No document type uses this template yet. Point one at it on the
              Types tab.
            </p>
          )}

          <Section title="Defaults">
            <div style={row}>
              <Labelled label="Body typeface">
                <select style={input} value={template.font_family}
                  onChange={(e) => set(['font_family'], e.target.value)}>
                  {FONT_FACES.map((face) => (
                    <option key={face.name} value={face.name}>
                      {face.name}{face.exact ? '' : ' (approximated in PDF)'}
                    </option>
                  ))}
                </select>
              </Labelled>
              <Labelled label="Body size (pt)">
                <input style={{ ...input, width: 90 }} type="number" step="0.5"
                  min={6} max={36} value={template.font_size}
                  onChange={(e) => set(['font_size'], Number(e.target.value))} />
              </Labelled>
              <Labelled label="Numbered lists">
                <select style={input} value={template.list_numbering}
                  onChange={(e) => set(['list_numbering'], e.target.value)}>
                  {LIST_NUMBERING.map((n) => (
                    <option key={n.value} value={n.value}>{n.label}</option>
                  ))}
                </select>
              </Labelled>
            </div>
            <Labelled label="Formatting"
              help="Locked means an author writes in the template's styles and cannot apply their own — which is what makes a controlled document look the same whoever wrote it. Individual elements can still be opened up below.">
              <select style={input} value={template.mode}
                onChange={(e) => set(['mode'], e.target.value)}>
                <option value="override">Authors may override formatting</option>
                <option value="enforce">Locked to the template</option>
              </select>
            </Labelled>
            <p style={help}>
              A typeface marked “approximated in PDF” is drawn with its closest
              PDF-core equivalent. The editor measures it the same way, so the
              page still breaks where the exported file breaks — but the letters
              are not literally that face. Arial, Times New Roman and Courier
              New are exact.
            </p>
          </Section>

          <Section title="Page">
            <div style={row}>
              <Labelled label="Width (in)">
                <input style={{ ...input, width: 90 }} type="number" step="0.01"
                  value={template.page_layout.page_width}
                  onChange={(e) => set(['page_layout', 'page_width'],
                    Number(e.target.value))} />
              </Labelled>
              <Labelled label="Height (in)">
                <input style={{ ...input, width: 90 }} type="number" step="0.01"
                  value={template.page_layout.page_height}
                  onChange={(e) => set(['page_layout', 'page_height'],
                    Number(e.target.value))} />
              </Labelled>
              <Labelled label="Left (in)">
                <input style={{ ...input, width: 90 }} type="number" step="0.1"
                  value={template.page_layout.left_margin}
                  onChange={(e) => set(['page_layout', 'left_margin'],
                    Number(e.target.value))} />
              </Labelled>
              <Labelled label="Right (in)">
                <input style={{ ...input, width: 90 }} type="number" step="0.1"
                  value={template.page_layout.right_margin}
                  onChange={(e) => set(['page_layout', 'right_margin'],
                    Number(e.target.value))} />
              </Labelled>
              <Labelled label="Top (pt)">
                <input style={{ ...input, width: 90 }} type="number" step="6"
                  value={template.page_layout.top_margin}
                  onChange={(e) => set(['page_layout', 'top_margin'],
                    Number(e.target.value))} />
              </Labelled>
              <Labelled label="Bottom (pt)">
                <input style={{ ...input, width: 90 }} type="number" step="6"
                  value={template.page_layout.bottom_margin}
                  onChange={(e) => set(['page_layout', 'bottom_margin'],
                    Number(e.target.value))} />
              </Labelled>
            </div>
            <p style={help}>
              Header and footer accept <code>{'{page}'}</code>,{' '}
              <code>{'{pages}'}</code>, <code>{'{title}'}</code> and{' '}
              <code>{'{date}'}</code>. An unrecognised token is printed as typed
              rather than blanked, so a mistake is visible.
            </p>
            {(['header', 'footer'] as const).map((band) => (
              <div style={row} key={band}>
                {(['left', 'center', 'right'] as const).map((side) => (
                  <Labelled key={side}
                    label={`${band === 'header' ? 'Header' : 'Footer'} ${side}`}>
                    <input style={input} value={template.page_layout[band][side]}
                      onChange={(e) => set(['page_layout', band, side],
                        e.target.value)} />
                  </Labelled>
                ))}
              </div>
            ))}
          </Section>

          <Section title="Element formatting"
            note="One rule per element. These are the values the editor renders with, the page-break calculation measures against, and the PDF is drawn from — there is no fourth opinion.">
            <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>
              <div style={{ width: 180, flexShrink: 0 }}>
                {RULE_GROUPS.map((group) => (
                  <div key={group.title} style={{ marginBottom: 10 }}>
                    <div style={{ ...groupTitle, marginBottom: 2 }}>
                      {group.title}
                    </div>
                    {group.elements.map((id) => (
                      <button key={id} type="button" onClick={() => setElement(id)}
                        style={{ ...listBtn, fontSize: TYPE.small,
                          background: element === id
                            ? 'var(--color-surface-2)' : 'transparent' }}>
                        {ELEMENT_LABELS[id] || id}
                      </button>
                    ))}
                  </div>
                ))}
              </div>
              <RuleEditor
                rule={template.rules[element]}
                element={element}
                enforced={template.mode === 'enforce'}
                onChange={(key, value) => set(['rules', element, key], value)}
              />
            </div>
          </Section>

          <ControlSectionEditor template={template} set={set} />

          <Section title="Sections a new document opens with"
            note="Ordinary content — an author deletes what the document does not need. An empty page is the reason business documents end up shaped by whoever wrote the first one.">
            <StarterEditor
              starter={template.starter}
              onChange={(next) => set(['starter'], next)}
            />
          </Section>
        </div>
      )}
    </div>
  )
}

function RuleEditor({ rule, element, enforced, onChange }: {
  rule: FormattingRule | undefined
  element: string
  enforced: boolean
  onChange: (key: string, value: unknown) => void
}) {
  if (!rule) {
    return <p style={mutedBox}>This template has no rule for {element}.</p>
  }
  return (
    <div style={{ flex: 1, minWidth: 0, display: 'grid', gap: 10 }}>
      <div style={row}>
        <Labelled label="Typeface" help="Empty uses the template's body face.">
          <select style={input} value={rule.font_family || ''}
            onChange={(e) => onChange('font_family', e.target.value || null)}>
            <option value="">Template default</option>
            {FONT_FACES.map((face) => (
              <option key={face.name} value={face.name}>{face.name}</option>
            ))}
          </select>
        </Labelled>
        <Labelled label="Size (pt)">
          <input style={{ ...input, width: 90 }} type="number" step="0.5"
            value={rule.font_size ?? ''} placeholder="default"
            onChange={(e) => onChange('font_size',
              e.target.value === '' ? null : Number(e.target.value))} />
        </Labelled>
        <Labelled label="Line height">
          <input style={{ ...input, width: 90 }} type="number" step="0.05"
            min={0.8} max={4} value={rule.line_height}
            onChange={(e) => onChange('line_height', Number(e.target.value))} />
        </Labelled>
      </div>

      <div style={row}>
        {(['bold', 'italic', 'underline', 'strikethrough'] as const).map((key) => (
          <label key={key} style={check}>
            <input type="checkbox" checked={Boolean(rule[key])}
              onChange={(e) => onChange(key, e.target.checked)} />
            {key === 'strikethrough' ? 'Strikethrough'
              : key[0].toUpperCase() + key.slice(1)}
          </label>
        ))}
      </div>

      <div style={row}>
        <Labelled label="Alignment">
          <select style={input} value={rule.text_align}
            onChange={(e) => onChange('text_align', e.target.value)}>
            <option value="left">Left</option>
            <option value="center">Centre</option>
            <option value="right">Right</option>
            <option value="justify">Justified</option>
          </select>
        </Labelled>
        <Labelled label="Capitalisation">
          <select style={input} value={rule.text_transform}
            onChange={(e) => onChange('text_transform', e.target.value)}>
            <option value="none">As typed</option>
            <option value="uppercase">UPPER CASE</option>
            <option value="lowercase">lower case</option>
            <option value="capitalize">Title Case</option>
          </select>
        </Labelled>
        <Labelled label="Text colour">
          <input style={{ ...input, width: 110 }} type="text"
            value={rule.text_color || ''} placeholder="#000000"
            onChange={(e) => onChange('text_color', e.target.value || null)} />
        </Labelled>
        <Labelled label="Background">
          <input style={{ ...input, width: 110 }} type="text"
            value={rule.background_color || ''} placeholder="none"
            onChange={(e) => onChange('background_color',
              e.target.value || null)} />
        </Labelled>
      </div>

      <div style={row}>
        <Labelled label="Space before (pt)">
          <input style={{ ...input, width: 100 }} type="number" step="1" min={0}
            value={rule.space_before}
            onChange={(e) => onChange('space_before', Number(e.target.value))} />
        </Labelled>
        <Labelled label="Space after (pt)">
          <input style={{ ...input, width: 100 }} type="number" step="1" min={0}
            value={rule.space_after}
            onChange={(e) => onChange('space_after', Number(e.target.value))} />
        </Labelled>
        <Labelled label="Left indent (in)">
          <input style={{ ...input, width: 100 }} type="number" step="0.05" min={0}
            value={rule.left_indent}
            onChange={(e) => onChange('left_indent', Number(e.target.value))} />
        </Labelled>
        <Labelled label="Right indent (in)">
          <input style={{ ...input, width: 100 }} type="number" step="0.05" min={0}
            value={rule.right_indent}
            onChange={(e) => onChange('right_indent', Number(e.target.value))} />
        </Labelled>
        <Labelled label="First line (in)">
          <input style={{ ...input, width: 100 }} type="number" step="0.05"
            value={rule.first_line_indent}
            onChange={(e) => onChange('first_line_indent',
              Number(e.target.value))} />
        </Labelled>
      </div>

      <div style={row}>
        <label style={check}>
          <input type="checkbox" checked={rule.keep_with_next}
            onChange={(e) => onChange('keep_with_next', e.target.checked)} />
          Keep with the next block
        </label>
        <label style={check}>
          <input type="checkbox" checked={rule.page_break_before}
            onChange={(e) => onChange('page_break_before', e.target.checked)} />
          Always start a new page
        </label>
        {enforced ? (
          <label style={check}>
            <input type="checkbox" checked={rule.allow_format_override}
              onChange={(e) => onChange('allow_format_override',
                e.target.checked)} />
            Authors may still emphasise text here
          </label>
        ) : null}
      </div>
      <p style={help}>
        “Keep with the next block” is what stops a heading being stranded as the
        last line of a page. It is worth leaving on for every heading.
      </p>
    </div>
  )
}

function ControlSectionEditor({ template, set }: {
  template: DocumentTemplate
  set: (path: string[], value: unknown) => void
}) {
  const control = template.control_section
  const rows = control.rows || []

  return (
    <Section title="Document control section"
      note="The block at the head of a controlled document. Each row is BOUND to where its value lives, so it is resolved from the record every time the document is opened or rendered — a typed version number is wrong the first time somebody forgets to change it.">
      <div style={row}>
        <label style={check}>
          <input type="checkbox" checked={control.enabled}
            onChange={(e) => set(['control_section', 'enabled'],
              e.target.checked)} />
          Include a control section
        </label>
        <label style={check}>
          <input type="checkbox" checked={control.show_revision_history}
            onChange={(e) => set(['control_section', 'show_revision_history'],
              e.target.checked)} />
          Include the revision history
        </label>
      </div>

      <div style={row}>
        <Labelled label="Heading">
          <input style={input} value={control.title}
            onChange={(e) => set(['control_section', 'title'], e.target.value)} />
        </Labelled>
        <Labelled label="Revision history heading">
          <input style={input} value={control.revision_history_title}
            onChange={(e) => set(['control_section', 'revision_history_title'],
              e.target.value)} />
        </Labelled>
        <Labelled label="Pairs per row">
          <select style={{ ...input, width: 90 }} value={control.columns}
            onChange={(e) => set(['control_section', 'columns'],
              Number(e.target.value))}>
            {[1, 2, 3, 4].map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
        </Labelled>
      </div>

      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: TYPE.small }}>
        <thead>
          <tr>
            {['Label', 'Comes from', 'Field / property', 'Fixed value', ''].map((h) => (
              <th key={h} style={th}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, index) => (
            <tr key={index}>
              <td style={td}>
                <input style={input} value={r.label}
                  onChange={(e) => set(['control_section', 'rows', String(index),
                    'label'], e.target.value)} />
              </td>
              <td style={td}>
                <select style={input} value={r.source}
                  onChange={(e) => set(['control_section', 'rows', String(index),
                    'source'], e.target.value)}>
                  {CONTROL_SOURCES.map((source) => (
                    <option key={source.value} value={source.value}>
                      {source.label}
                    </option>
                  ))}
                </select>
              </td>
              <td style={td}>
                {r.source === 'document' ? (
                  <select style={input} value={r.key}
                    onChange={(e) => set(['control_section', 'rows',
                      String(index), 'key'], e.target.value)}>
                    <option value="">Choose…</option>
                    {DOCUMENT_KEYS.map((key) => (
                      <option key={key} value={key}>{key}</option>
                    ))}
                  </select>
                ) : r.source === 'static' ? (
                  <span style={{ color: 'var(--color-text-secondary)' }}>—</span>
                ) : (
                  <input style={input} value={r.key}
                    placeholder={r.source === 'metadata' ? 'OWNER' : 'APPROVED_BY'}
                    onChange={(e) => set(['control_section', 'rows',
                      String(index), 'key'], e.target.value.toUpperCase())} />
                )}
              </td>
              <td style={td}>
                <input style={input} value={r.value}
                  placeholder={r.source === 'static' ? 'Confidential' : ''}
                  disabled={r.source === 'document' || r.source === 'metadata'}
                  onChange={(e) => set(['control_section', 'rows', String(index),
                    'value'], e.target.value)} />
              </td>
              <td style={td}>
                <button type="button" style={btn}
                  onClick={() => set(['control_section', 'rows'],
                    rows.filter((_, i) => i !== index))}>Remove</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <button type="button" style={btn}
        onClick={() => set(['control_section', 'rows'], [...rows, {
          label: 'New row', source: 'static', key: '', value: '',
        }])}>Add row</button>
      <p style={help}>
        A metadata row that is bound to a SECURE field renders its mask for a
        reader outside the field's reveal roles — the control block is printed
        and exported, so it must not be the one part of the page that leaks a
        value.
      </p>
    </Section>
  )
}

/**
 * The starter document, edited as the list of sections it is.
 *
 * Deliberately not a second rich-text editor. What a template seeds is a run of
 * headings and prompts; giving it the full editor would invite formatting that
 * the template's own rules are supposed to decide.
 */
function StarterEditor({ starter, onChange }: {
  starter: any[]
  onChange: (next: any[]) => void
}) {
  const describe = (node: any): { kind: string; text: string } => {
    if (node?.type === 'controlSection') {
      return { kind: 'control', text: 'The document control block' }
    }
    const text = (node?.content || [])
      .map((c: any) => c?.text || '').join('')
    if (node?.type === 'heading') {
      return { kind: `heading${node.attrs?.level || 1}`, text }
    }
    return { kind: String(node?.attrs?.styleId || 'body'), text }
  }

  const update = (index: number, kind: string, text: string) => {
    const next = [...starter]
    if (kind === 'control') {
      next[index] = { type: 'controlSection', attrs: { values: {} } }
    } else {
      const match = /^heading([1-4])$/.exec(kind)
      const content = text ? [{ type: 'text', text }] : undefined
      next[index] = match
        ? { type: 'heading', attrs: { level: Number(match[1]) }, content }
        : { type: 'paragraph', attrs: { styleId: kind }, content }
    }
    onChange(next)
  }

  return (
    <>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: TYPE.small }}>
        <tbody>
          {starter.map((node, index) => {
            const { kind, text } = describe(node)
            return (
              <tr key={index}>
                <td style={{ ...td, width: 150 }}>
                  <select style={input} value={kind}
                    onChange={(e) => update(index, e.target.value, text)}>
                    <option value="control">Document control block</option>
                    <option value="title">Title</option>
                    <option value="subtitle">Subtitle</option>
                    <option value="heading1">Heading 1</option>
                    <option value="heading2">Heading 2</option>
                    <option value="heading3">Heading 3</option>
                    <option value="heading4">Heading 4</option>
                    <option value="body">Body text</option>
                    <option value="caption">Caption</option>
                  </select>
                </td>
                <td style={td}>
                  <input style={input} value={text}
                    disabled={kind === 'control'}
                    placeholder={kind === 'control'
                      ? 'Resolved from the record' : 'Text or prompt'}
                    onChange={(e) => update(index, kind, e.target.value)} />
                </td>
                <td style={{ ...td, width: 150 }}>
                  <button type="button" style={btn} disabled={index === 0}
                    onClick={() => {
                      const next = [...starter]
                      const [moved] = next.splice(index, 1)
                      next.splice(index - 1, 0, moved)
                      onChange(next)
                    }}>↑</button>
                  <button type="button" style={btn}
                    disabled={index === starter.length - 1}
                    onClick={() => {
                      const next = [...starter]
                      const [moved] = next.splice(index, 1)
                      next.splice(index + 1, 0, moved)
                      onChange(next)
                    }}>↓</button>
                  <button type="button" style={btn}
                    onClick={() => onChange(starter.filter((_, i) => i !== index))}>
                    Remove
                  </button>
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
      <button type="button" style={btn}
        onClick={() => onChange([...starter, {
          type: 'heading', attrs: { level: 1 },
          content: [{ type: 'text', text: 'New section' }],
        }])}>Add section</button>
    </>
  )
}

// ── presentation ──────────────────────────────────────────────────────────
// These names stay local, but their VALUES come from
// `@twasta/page-runtime/dms/styles` — the same vocabulary the repository
// screens render with. They used to be a private, hand-tuned copy, on the
// reasoning that importing the designer page's constants would leak a private
// detail into its surface. That reasoning no longer applies: the shared module
// exists precisely to be imported, and a private copy is how the templates tab
// drifted to 11.5px help text and uppercase micro-caps while everything else
// moved on.

function Labelled({ label, help: hint, children }: {
  label: string; help?: string; children: React.ReactNode
}) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 2,
      fontSize: TYPE.meta, minWidth: 0 }}>
      <span style={dmsLabelStyle}>{label}</span>
      {children}
      {hint ? <span style={help}>{hint}</span> : null}
    </label>
  )
}

function Section({ title, note, children }: {
  title: string; note?: string; children: React.ReactNode
}) {
  return (
    <section style={{ ...dmsCardStyle, display: 'grid', gap: 12 }}>
      <h3 style={groupTitle}>{title}</h3>
      {note ? <p style={help}>{note}</p> : null}
      {children}
    </section>
  )
}

const input: React.CSSProperties = {
  ...dmsInputStyle, width: '100%', minWidth: 0,
}

const btn: React.CSSProperties = { ...dmsBtnStyle, marginRight: 4 }

const listBtn: React.CSSProperties = {
  display: 'block', width: '100%', textAlign: 'left', padding: '8px 10px',
  border: 'none', borderRadius: RADIUS.md, cursor: 'pointer', color: T.text,
  fontSize: TYPE.meta, fontFamily: 'inherit',
}

const row: React.CSSProperties = {
  display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end',
}

const check: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 6, fontSize: TYPE.small,
  color: T.textSecondary, whiteSpace: 'nowrap',
}

const groupTitle: React.CSSProperties = {
  margin: 0, fontSize: TYPE.heading, fontWeight: 600, color: T.text,
}

const help: React.CSSProperties = {
  margin: 0, fontSize: TYPE.small, color: T.textSecondary, lineHeight: 1.55,
}

const mutedBox: React.CSSProperties = {
  padding: 20, fontSize: TYPE.body, color: T.textSecondary,
}

const th: React.CSSProperties = {
  textAlign: 'left', padding: '8px 10px', fontWeight: 600,
  fontSize: TYPE.small, whiteSpace: 'nowrap',
  borderBottom: `1px solid ${T.border}`,
  color: T.textFaint,
}

const td: React.CSSProperties = {
  padding: '8px 10px', verticalAlign: 'top', lineHeight: 1.5,
  borderBottom: `1px solid ${T.border}`,
}

export default TemplatesTab
